import React from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import capitalize from '@material-ui/core/utils/capitalize';
import ReactDOMServer from 'react-dom/server';
import Big from 'big-js';
import PropTypes from 'prop-types';
import { INTL_VOLUME_FORMAT } from '../../App';
import { BOUGHT_CHART_INTL_KEY } from '../BoughtChart/contants';
import { GRAPH_VOLUME_COLOR, WHITE } from '../../constants/colors';
import { getMonthName } from '../../utils/date';
import { CORNER_RADIUS } from '../../styles/theme';
import { EMPTY_CONTRACTED_YEAR_DATA } from '../BoughtChart/boughtChartUtil';
import Chart from './Chart';
import { createXAxis, createYAxis, graphColumns } from './utils/utils';
import ChartTooltip from './components/ChartTooltip';
import { convertToChartData } from '../BoughtChart/convertToChartData';
import { parseNumber } from '../../utils/number';

const findQuantityByProductTypeAndMonth = (dataArray, month, productType) => {
  return dataArray?.find((item) => item.productType === productType)?.quantities?.find((item) => item.month === month)?.quantity || 0;
};

const getPercents = (total, part) => {
  const totalBig = new Big(total);
  const partBig = new Big(part);

  const percents = totalBig.cmp(0) ? partBig.times(100).div(totalBig) : new Big(100);
  return percents.toFixed(2);
};

const sortByOrderArray = (inputArray, orderArray) => {
  return inputArray.sort((a, b) => (orderArray.indexOf(a.productType) > orderArray.indexOf(b.productType) ? 1 : -1));
};

const prepareChartData = (data, chartData, columnLayersConfig, seriesConfig, orderArray) => {
  return (
    data &&
    sortByOrderArray(
      data?.map((item) => {
        return {
          ...columnLayersConfig[item.productType],
          color: seriesConfig[item.productType]?.color,
          name: seriesConfig[item.productType]?.name,
          dataPoints: chartData[item.productType],
          productType: item.productType,
        };
      }),
      orderArray,
    )
  );
};

const prepareTooltip = (intl, data, tooltipOrder, seriesConfig, mandatory, translationPrefix) => {
  const tooltipData = [];

  for (let i = 0; i <= 11; i++) {
    const productTypesQuantities = Object.values(graphColumns).reduce((accumulator, value) => {
      accumulator[value] = findQuantityByProductTypeAndMonth(data, i + 1, value);
      return accumulator;
    }, {});

    const totalBig = new Big(productTypesQuantities[graphColumns.CONTRACT]);
    const tradedBig = new Big(productTypesQuantities[graphColumns.ANNUAL] || 0)
      .plus(productTypesQuantities[graphColumns.QUARTER] || 0)
      .plus(productTypesQuantities[graphColumns.MONTH] || 0)
      .minus(new Big(productTypesQuantities[graphColumns.RELEASE] || 0).abs());

    const totalPercent = getPercents(totalBig, tradedBig);

    const recalc = tooltipOrder.reduce((accumulator, item) => {
      const value = productTypesQuantities[item];
      if (value !== 0 || mandatory.indexOf(item) !== -1) {
        accumulator.push({
          productType: item,
          percent: getPercents(totalBig, productTypesQuantities[item]),
          value: intl.formatMessage(
            {
              id: `${translationPrefix}.tooltip.value`,
              defaultMessage: '{percentage}% ({volume} MWh)',
            },
            {
              percentage: intl.formatNumber(getPercents(totalBig, value), {
                format: INTL_VOLUME_FORMAT,
              }),
              volume: intl.formatNumber(value.toFixed(3), {
                format: INTL_VOLUME_FORMAT,
              }),
            },
          ),
          label: seriesConfig[item].tooltipLabel,
          color: seriesConfig[item].color,
        });
      }
      return accumulator;
    }, []);

    const monthTooltip = [
      {
        color: seriesConfig[graphColumns.CONTRACT].color,
        label: intl.formatMessage({
          id: `${translationPrefix}.tooltip.total_label`,
          defaultMessage: 'Contract',
        }),
        value: intl.formatMessage(
          {
            id: `${translationPrefix}.tooltip.total_value`,
            defaultMessage: '{percentage}% ({volume} MWh of {contractedVolume} MWh)',
          },
          {
            percentage: intl.formatNumber(totalPercent, {
              format: INTL_VOLUME_FORMAT,
            }),
            volume: intl.formatNumber(tradedBig.toFixed(3), {
              format: INTL_VOLUME_FORMAT,
            }),
            contractedVolume: intl.formatNumber(totalBig.toFixed(3), {
              format: INTL_VOLUME_FORMAT,
            }),
          },
        ),
      },
      ...recalc,
    ];

    tooltipData.push(monthTooltip);
  }

  return tooltipData;
};

const prepareLegend = (legendOrder, seriesConfig, data, mandatory) => {
  return legendOrder.reduce((accumulator, item) => {
    if (mandatory.indexOf(item) !== -1 || data?.find((dataObj) => dataObj.productType === item)) {
      accumulator.push({
        color: seriesConfig[item].color,
        name: seriesConfig[item].legendLabel,
      });
    }
    return accumulator;
  }, []);
};

export const adjust = (value, totalTraded, totalReleased) => {
  if (!new Big(totalReleased).cmp(0)) {
    return new Big(value).toFixed(10);
  }
  if (!new Big(value).cmp(0)) {
    return new Big(0).toFixed(10);
  }
  let output = new Big(value).minus(new Big(totalReleased).times(new Big(value).div(totalTraded)));
  if (!output.gt(new Big(0))) {
    output = new Big(0);
  }

  return output.toFixed(10);
};

export const recalculateQuantitiesUsingReleasedQuantities = (nonNullData) => {
  const months = [];
  for (let i = 1; i <= 12; i++) {
    months.push({
      month: i,
      values: {},
    });
  }

  const temp = months.reduce((output, monthObj) => {
    const monthValues = nonNullData.reduce((output, item) => {
      output[item.productType] = item.quantities.find((quantityObj) => {
        return quantityObj.month === monthObj.month;
      })?.quantity || 0;
      return output;
    }, {});
    output.push({
      month: monthObj.month,
      values: { ...monthValues },
    });

    return output;
  }, []);

  const adjusted = temp.map((month) => {
    const totalTraded = new Big(month.values[graphColumns.ANNUAL] || 0)
      .plus(month.values[graphColumns.QUARTER] || 0)
      .plus(month.values[graphColumns.MONTH] || 0);

    const totalReleased = new Big(month.values[graphColumns.RELEASE] || 0).abs().plus(month.values[graphColumns.RELEASING] || 0);

    const colsToAdjust = [graphColumns.ANNUAL, graphColumns.QUARTER, graphColumns.MONTH];
    const tmp = colsToAdjust.reduce((output, col) => {
      if (month.values[col]) {
        output[col] = parseNumber(adjust(new Big(month.values[col]), totalTraded, totalReleased));
      }


      return output;
    }, {});
    return { ...month, values: { ...month.values, ...tmp } };
  });

  const adjustedNonNullDataObject = {};

  adjusted.forEach((item) => {
    Object.keys(item.values).forEach((key) => {
      if (!adjustedNonNullDataObject[key]) {
        adjustedNonNullDataObject[key] = [];
      }
      adjustedNonNullDataObject[key].push({
        month: item.month,
        quantity: item.values[key],
      });
    });
  });

  return Object.keys(adjustedNonNullDataObject).reduce((output, key) => {
    output.push({
      productType: key,
      quantities: adjustedNonNullDataObject[key],
    });
    return output;
  }, []);
};

const mandatory = [graphColumns.CONTRACT, graphColumns.ANNUAL, graphColumns.QUARTER, graphColumns.MONTH, graphColumns.RELEASE];
const tooltipOrder = [graphColumns.TRADING, graphColumns.RELEASING, graphColumns.MONTH, graphColumns.QUARTER, graphColumns.ANNUAL, graphColumns.RELEASE];
const legendOrder = [
  graphColumns.TRADING,
  graphColumns.RELEASING,
  graphColumns.MONTH,
  graphColumns.QUARTER,
  graphColumns.ANNUAL,
  graphColumns.RELEASE,
  graphColumns.CONTRACT,
];
const columnLayersOrder = [
  graphColumns.CONTRACT,
  graphColumns.ANNUAL,
  graphColumns.QUARTER,
  graphColumns.MONTH,
  graphColumns.TRADING,
  graphColumns.RELEASE,
  graphColumns.RELEASING,
];

const TradeChart = ({ className, data, year, config, seriesConfig, translationPrefix = BOUGHT_CHART_INTL_KEY }) => {
  const intl = useIntl();
  const theme = useTheme();
  const graphTheme = theme.graph;

  const configNotNull = config || {};
  const nonNullData = data ? data : EMPTY_CONTRACTED_YEAR_DATA;
  const adjustedNonNullData = recalculateQuantitiesUsingReleasedQuantities(nonNullData);

  const chartData = convertToChartData(adjustedNonNullData, year);

  const columnLayersConfig = {
    [graphColumns.CONTRACT]: {
      type: 'column',
      indexLabelFormatter: (e) => intl.formatNumber(e.dataPoint.y),
      indexLabel: '{y}',
      indexLabelFontFamily: graphTheme.baseFontFamily,
      indexLabelFontSize: graphTheme.indexLabelSize,
      indexLabelFontColor: graphTheme.baseFontColor,
      indexLabelPlacement: 'outside',
      indexLabelOrientation: 'horizontal',
    },
    [graphColumns.ANNUAL]: {
      type: 'stackedColumn',
    },
    [graphColumns.QUARTER]: {
      type: 'stackedColumn',
    },
    [graphColumns.MONTH]: {
      type: 'stackedColumn',
    },
    [graphColumns.TRADING]: {
      type: 'stackedColumn',
    },
    [graphColumns.RELEASE]: {
      type: 'stackedColumn',
    },
    [graphColumns.RELEASING]: {
      type: 'stackedColumn',
    },
  };

  const outputChartData = prepareChartData(data, chartData, columnLayersConfig, seriesConfig, columnLayersOrder);
  const tooltipData = prepareTooltip(intl, data, tooltipOrder, seriesConfig, mandatory, translationPrefix);
  const legend = prepareLegend(legendOrder, seriesConfig, data, mandatory);

  const options = {
    animationEnabled: true,
    axisY: createYAxis({
      theme: graphTheme,
      hideYAxis: configNotNull.hideYAxesName,
      title: !configNotNull.hideYAxesScale ?
        intl.formatMessage({
          id: `${translationPrefix}.volume_in_MWH`,
          defaultMessage: intl.formatMessage({ id: `app.common.volumeMwh` }),
        }) : '',
      labelFormatter: (e) => (!configNotNull.hideYAxesScale ? intl.formatNumber(e.value) : ''),
    }),
    axisX: createXAxis({
      theme: graphTheme,
      tickLength: 15,
      tickColor: WHITE,
      interval: 1,
      intervalType: 'month',
      lineColor: GRAPH_VOLUME_COLOR,
      labelFormatter: (e) => {
        return capitalize(getMonthName(intl, e.value, 'short'));
      },
    }),
    toolTip: {
      shared: true,
      borderThickness: 0,
      cornerRadius: CORNER_RADIUS,
      content: ({ entries }) => {
        const columnIndex = entries[0].index;
        const month = capitalize(getMonthName(intl, new Date(year, columnIndex, 1)));
        return ReactDOMServer.renderToString(<ChartTooltip data={tooltipData[columnIndex]} month={month} />);
      },
    },
    data: outputChartData,
  };

  return <Chart className={className} legendItems={legend} options={options} />;
};

TradeChart.propTypes = {
  className: PropTypes.string,
  year: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      productType: PropTypes.string,
      quantities: PropTypes.arrayOf(
        PropTypes.shape({
          month: PropTypes.number,
          quantity: PropTypes.number,
        }),
      ),
    }),
  ),
  theme: PropTypes.shape({
    graph: PropTypes.shape({
      baseFontFamily: PropTypes.string,
      baseFontSize: PropTypes.number,
      baseFontColor: PropTypes.string,
      indexLabelSize: PropTypes.number,
    }),
  }),
  config: PropTypes.shape({
    hideYAxesScale: PropTypes.bool,
    hideYAxesName: PropTypes.bool,
  }),
  seriesConfig: PropTypes.object,
  translationPrefix: PropTypes.string,
};

export default TradeChart;
